import { useEffect, useMemo, useState, type ComponentType, type ReactNode } from "react";
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router";
import {
  Activity,
  ArrowLeft,
  BarChart3,
  BookOpenCheck,
  CalendarDays,
  CheckCircle2,
  ClipboardList,
  Clock3,
  CreditCard,
  FileText,
  GraduationCap,
  Headphones,
  Home,
  Languages,
  ListChecks,
  LockKeyhole,
  LogOut,
  PenLine,
  Play,
  ShieldCheck,
  Target,
  Trophy,
  UserRound,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { ApiUser, apiFetch, postJson } from "@/lib/cloudApi";

type StudentLocale = "en" | "tr" | "de";

type StudentTextKey = keyof typeof studentText.en;

type StudentSubscription = {
  id?: string;
  active: boolean;
  status: string;
  currentPeriodEnd?: string | null;
  planName: string;
  planCode: string;
  amountCents?: number;
  currency?: string;
  interval?: string;
  licenseKey: string;
} | null;

type StudentDashboardStats = {
  totalAttempts: number;
  completedAttempts: number;
  bestPercent: number;
  averagePercent: number;
  studyMinutes: number;
};

type StudentSkillPerformance = {
  skillKey: string;
  skillName: string;
  answeredCount: number;
  autoAnsweredCount: number;
  correctCount: number;
  textAnswerCount: number;
  performance: number | null;
};

type StudentRecentAttempt = {
  id: string;
  status: string;
  scoreRaw: number;
  scoreScaled: number;
  startedAt: string;
  finishedAt?: string | null;
  examTitle: string;
  templateName: string;
  familyName: string;
  levelCode: string;
};

type StudentMockExam = {
  id: string;
  code: string;
  title: string;
  visibility: string;
  taskCount: number;
  questionCount: number;
  pointTotal: number;
  progress: {
    attemptCount: number;
    bestScore: number;
    bestPercent: number;
    lastStatus?: string | null;
    lastStartedAt?: string | null;
  };
  template: {
    id: string;
    code: string;
    name: string;
    levelCode: string;
    durationSec: number;
    totalPoints: number;
    family: { code: string; name: string };
    language: { code: string; name: string; nativeName: string };
  };
};

type StudentHomePayload = {
  hasActiveSubscription: boolean;
  subscription: StudentSubscription;
  stats: StudentDashboardStats;
  skillPerformance: StudentSkillPerformance[];
  recentAttempts: StudentRecentAttempt[];
  mockExams: StudentMockExam[];
};

type StudentChoice = {
  id: string;
  text: string;
  sortOrder: number;
};

type StudentQuestion = {
  id: string;
  sortOrder: number;
  points: number;
  title: string;
  stem: string;
  explanation?: string;
  skillKey: string;
  taskType: string;
  metadata?: Record<string, unknown>;
  choices: StudentChoice[];
};

type StudentTask = {
  id: string;
  sortOrder: number;
  sectionName: string;
  sectionInstructions?: string;
  skill: { key: string; name: string; durationSec: number };
  templateTask: {
    id: string;
    key: string;
    type: string;
    name: string;
    prompt?: string;
    itemCount: number;
    maxPoints: number;
    settings?: Record<string, unknown>;
  };
  questionCount: number;
  pointTotal: number;
  questions: StudentQuestion[];
};

type StudentMockDetail = {
  id: string;
  code: string;
  title: string;
  visibility: string;
  taskCount: number;
  questionCount: number;
  pointTotal: number;
  template: {
    id: string;
    code: string;
    name: string;
    description?: string;
    levelCode: string;
    durationSec: number;
    totalPoints: number;
    passingScore: number;
    family: { code: string; name: string };
    language: { code: string; name: string; nativeName: string };
  };
  tasks: StudentTask[];
};

type AttemptResult = {
  attemptId: string;
  status: string;
  scoreRaw: number;
  scoreScaled: number | null;
  maxAutoScore: number;
  autoQuestionCount: number;
  correctCount: number;
  finishedAt: string;
  review: Array<{
    questionId: string;
    choiceId?: string | null;
    correct: number | null;
    correctChoiceIds: string[];
    explanation?: string;
  }>;
};

type AnswerDraft = {
  choiceId?: string;
  textAnswer?: string;
};

const studentText = {
  en: {
    portal: "Student portal",
    subtitle: "Your exam workspace, package, license and performance in one place.",
    dashboard: "Dashboard",
    navDashboard: "Dashboard",
    navMyExams: "My exams",
    navPackage: "Package & license",
    navPerformance: "Performance",
    navAccount: "Account",
    exams: "Practice exams",
    myExams: "Purchased exams",
    examsDesc: "Your available practice exams and learning tracks.",
    available: "Available now",
    subscriptionReady: "Subscription active",
    subscriptionLocked: "Subscription required",
    start: "Start exam",
    continue: "Continue",
    review: "Review",
    attempts: "Attempts",
    completed: "Completed",
    best: "Best score",
    averageScore: "Average score",
    studyTime: "Study time",
    questions: "Questions",
    parts: "Parts",
    points: "Points",
    minutes: "min",
    language: "Language",
    level: "Level",
    signOut: "Sign out",
    back: "Back",
    loading: "Loading student workspace...",
    noExams: "No published practice exams yet.",
    noExamsDesc: "Publish a practice exam in the admin area to make it appear here.",
    overviewTitle: "Welcome back",
    overviewText: "Track your package, continue purchased exams and monitor performance by skill.",
    packageTitle: "Package & license",
    plan: "Plan",
    license: "License",
    validUntil: "Valid until",
    activeLicense: "Active license",
    inactiveLicense: "Inactive license",
    included: "Included",
    includedText: "Practice exams, saved attempts and performance tracking.",
    performanceTitle: "Skill performance",
    writingPerformance: "Writing performance",
    manualReview: "Manual review",
    notStarted: "Not started",
    recentActivity: "Recent activity",
    noActivity: "No exam attempt yet.",
    accountTitle: "Account",
    accountDesc: "Your learning profile and access status.",
    memberType: "Access type",
    openExam: "Open exam",
    timer: "Time left",
    answered: "Answered",
    currentPart: "Current part",
    previous: "Previous",
    next: "Next",
    submit: "Submit exam",
    submitting: "Submitting...",
    writeAnswer: "Write your answer here...",
    selectAnswer: "Select one answer",
    speakingNote: "Use this box for speaking notes or a transcript while recording support is added.",
    listeningNote: "Listening part",
    listeningHelp: "Audio files will appear inside this exam part when attached by the admin.",
    resultTitle: "Result",
    correct: "correct",
    autoScored: "auto-scored questions",
    notAutoScored: "Writing and speaking answers are saved for manual review.",
    retake: "Start another attempt",
    unansweredWarning: "Some questions are still unanswered.",
    submitFailed: "Exam could not be submitted",
    loadFailed: "Student data could not be loaded",
    started: "Attempt ready",
    saved: "Your answers were saved",
  },
  tr: {
    portal: "Ogrenci paneli",
    subtitle: "Sinav alani, paket, lisans ve performansin tek yerde.",
    dashboard: "Panel",
    navDashboard: "Panel",
    navMyExams: "Sinavlarim",
    navPackage: "Paket ve lisans",
    navPerformance: "Performans",
    navAccount: "Hesap",
    exams: "Deneme sinavlari",
    myExams: "Satin aldigin sinavlar",
    examsDesc: "Kullanabilecegin deneme sinavlari ve calisma paketleri.",
    available: "Kullanima acik",
    subscriptionReady: "Abonelik aktif",
    subscriptionLocked: "Abonelik gerekli",
    start: "Sinava basla",
    continue: "Devam et",
    review: "Incele",
    attempts: "Deneme",
    completed: "Tamamlanan",
    best: "En iyi skor",
    averageScore: "Ortalama skor",
    studyTime: "Calisma suresi",
    questions: "Soru",
    parts: "Bolum",
    points: "Puan",
    minutes: "dk",
    language: "Dil",
    level: "Seviye",
    signOut: "Cikis",
    back: "Geri",
    loading: "Ogrenci alani yukleniyor...",
    noExams: "Henuz yayinda deneme sinavi yok.",
    noExamsDesc: "Admin alaninda bir deneme sinavini yayinlayinca burada gorunur.",
    overviewTitle: "Tekrar hos geldin",
    overviewText: "Paketini takip et, satin aldigin sinavlara devam et ve beceri bazli performansini izle.",
    packageTitle: "Paket ve lisans",
    plan: "Paket",
    license: "Lisans",
    validUntil: "Gecerli tarih",
    activeLicense: "Aktif lisans",
    inactiveLicense: "Pasif lisans",
    included: "Dahil olanlar",
    includedText: "Deneme sinavlari, kayitli denemeler ve performans takibi.",
    performanceTitle: "Beceri performansi",
    writingPerformance: "Yazma performansi",
    manualReview: "Manuel inceleme",
    notStarted: "Baslamadi",
    recentActivity: "Son aktiviteler",
    noActivity: "Henuz sinav denemesi yok.",
    accountTitle: "Hesap",
    accountDesc: "Ogrenme profilin ve erisim durumun.",
    memberType: "Erisim tipi",
    openExam: "Sinavi ac",
    timer: "Kalan sure",
    answered: "Cevaplandi",
    currentPart: "Aktif bolum",
    previous: "Onceki",
    next: "Sonraki",
    submit: "Sinavi gonder",
    submitting: "Gonderiliyor...",
    writeAnswer: "Cevabini buraya yaz...",
    selectAnswer: "Bir cevap sec",
    speakingNote: "Kayit destegi eklenene kadar konusma notlarini veya transkripti burada tutabilirsin.",
    listeningNote: "Dinleme bolumu",
    listeningHelp: "Admin dosya eklediginde sesler ilgili sinav bolumu icinde gorunecek.",
    resultTitle: "Sonuc",
    correct: "dogru",
    autoScored: "otomatik puanlanan soru",
    notAutoScored: "Yazma ve konusma cevaplari manuel inceleme icin kaydedildi.",
    retake: "Yeni deneme baslat",
    unansweredWarning: "Bazi sorular henuz cevaplanmadi.",
    submitFailed: "Sinav gonderilemedi",
    loadFailed: "Ogrenci verisi alinamadi",
    started: "Deneme hazir",
    saved: "Cevaplarin kaydedildi",
  },
  de: {
    portal: "Lernbereich",
    subtitle: "Pruefungen, Paket, Lizenz und Leistung an einem Ort.",
    dashboard: "Uebersicht",
    navDashboard: "Uebersicht",
    navMyExams: "Meine Pruefungen",
    navPackage: "Paket & Lizenz",
    navPerformance: "Leistung",
    navAccount: "Konto",
    exams: "Probepruefungen",
    myExams: "Gekaufte Pruefungen",
    examsDesc: "Deine verfuegbaren Probepruefungen und Lernpakete.",
    available: "Jetzt verfuegbar",
    subscriptionReady: "Abo aktiv",
    subscriptionLocked: "Abo erforderlich",
    start: "Pruefung starten",
    continue: "Fortsetzen",
    review: "Pruefen",
    attempts: "Versuche",
    completed: "Abgeschlossen",
    best: "Beste Punktzahl",
    averageScore: "Durchschnitt",
    studyTime: "Lernzeit",
    questions: "Fragen",
    parts: "Teile",
    points: "Punkte",
    minutes: "Min.",
    language: "Sprache",
    level: "Niveau",
    signOut: "Abmelden",
    back: "Zurueck",
    loading: "Lernbereich wird geladen...",
    noExams: "Noch keine veroeffentlichten Probepruefungen.",
    noExamsDesc: "Veroeffentliche im Admin-Bereich eine Probepruefung, damit sie hier erscheint.",
    overviewTitle: "Willkommen zurueck",
    overviewText: "Paket verfolgen, gekaufte Pruefungen fortsetzen und Leistung nach Kompetenz sehen.",
    packageTitle: "Paket & Lizenz",
    plan: "Paket",
    license: "Lizenz",
    validUntil: "Gueltig bis",
    activeLicense: "Aktive Lizenz",
    inactiveLicense: "Inaktive Lizenz",
    included: "Enthalten",
    includedText: "Probepruefungen, gespeicherte Versuche und Leistungsuebersicht.",
    performanceTitle: "Kompetenzleistung",
    writingPerformance: "Schreibleistung",
    manualReview: "Manuelle Bewertung",
    notStarted: "Nicht begonnen",
    recentActivity: "Letzte Aktivitaeten",
    noActivity: "Noch kein Pruefungsversuch.",
    accountTitle: "Konto",
    accountDesc: "Dein Lernprofil und Zugangsstatus.",
    memberType: "Zugangstyp",
    openExam: "Pruefung oeffnen",
    timer: "Verbleibende Zeit",
    answered: "Beantwortet",
    currentPart: "Aktueller Teil",
    previous: "Zurueck",
    next: "Weiter",
    submit: "Pruefung abgeben",
    submitting: "Wird gesendet...",
    writeAnswer: "Antwort hier schreiben...",
    selectAnswer: "Eine Antwort waehlen",
    speakingNote: "Nutze dieses Feld fuer Sprechnotizen oder ein Transkript, bis Aufnahme-Support folgt.",
    listeningNote: "Hoerteil",
    listeningHelp: "Audiodateien erscheinen direkt in diesem Pruefungsteil, wenn sie im Adminbereich angehaengt werden.",
    resultTitle: "Ergebnis",
    correct: "richtig",
    autoScored: "automatisch bewertete Fragen",
    notAutoScored: "Schreiben und Sprechen wurden fuer manuelle Bewertung gespeichert.",
    retake: "Neuen Versuch starten",
    unansweredWarning: "Einige Fragen sind noch unbeantwortet.",
    submitFailed: "Pruefung konnte nicht abgegeben werden",
    loadFailed: "Lerndaten konnten nicht geladen werden",
    started: "Versuch bereit",
    saved: "Deine Antworten wurden gespeichert",
  },
} satisfies Record<StudentLocale, Record<string, string>>;

function useStudentLocale() {
  const [locale, setLocaleState] = useState<StudentLocale>(() => {
    if (typeof window === "undefined") return "en";
    const saved = window.localStorage.getItem("student_locale");
    return saved === "tr" || saved === "de" ? saved : "en";
  });

  const setLocale = (nextLocale: StudentLocale) => {
    setLocaleState(nextLocale);
    window.localStorage.setItem("student_locale", nextLocale);
  };

  return {
    locale,
    setLocale,
    t: (key: StudentTextKey) => studentText[locale][key] || studentText.en[key],
  };
}

export default function StudentPortal() {
  const localeState = useStudentLocale();

  return (
    <Routes>
      <Route index element={<StudentHome {...localeState} />} />
      <Route path="exam/:id" element={<StudentExam {...localeState} />} />
      <Route path="*" element={<Navigate to="/student" replace />} />
    </Routes>
  );
}

function StudentShell({
  children,
  locale,
  setLocale,
  t,
}: {
  children: ReactNode;
  locale: StudentLocale;
  setLocale: (locale: StudentLocale) => void;
  t: (key: StudentTextKey) => string;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<ApiUser | null>(null);

  useEffect(() => {
    apiFetch<{ user: ApiUser | null }>("/api/auth/me")
      .then((data) => setUser(data.user))
      .catch(() => setUser(null));
  }, []);

  const logout = async () => {
    await postJson("/api/auth/logout", {});
    window.location.href = "/login";
  };

  const nav = [
    { to: "/student", label: t("navDashboard"), icon: Home, hash: "" },
    { to: "/student#my-exams", label: t("navMyExams"), icon: ClipboardList, hash: "#my-exams" },
    { to: "/student#package", label: t("navPackage"), icon: CreditCard, hash: "#package" },
    { to: "/student#performance", label: t("navPerformance"), icon: BarChart3, hash: "#performance" },
    { to: "/student#account", label: t("navAccount"), icon: UserRound, hash: "#account" },
  ];

  return (
    <div className="min-h-screen bg-[#f8f4ee] text-[#171717] lg:grid lg:grid-cols-[282px_1fr]">
      <aside className="hidden border-r border-[#e2d8cd] bg-white lg:block">
        <div className="sticky top-0 flex h-screen flex-col">
          <button onClick={() => navigate("/student")} className="flex items-center gap-3 border-b border-[#eee4da] px-5 py-5 text-left">
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-[#d94205] text-sm font-bold text-white">BO</span>
            <span>
              <span className="block font-semibold">Exam Studio</span>
              <span className="block text-xs text-muted-foreground">{t("portal")}</span>
            </span>
          </button>
          <nav className="flex-1 space-y-1 p-3">
            {nav.map((item) => {
              const Icon = item.icon;
              const active = location.pathname === "/student" && (item.hash ? location.hash === item.hash : !location.hash);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={cn(
                    "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
                    active ? "bg-[#252525] text-white" : "text-[#3d3d3d] hover:bg-[#f4eee7]"
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
          <div className="border-t border-[#eee4da] p-4">
            <div className="mb-3 rounded-xl bg-[#fffaf4] p-3">
              <div className="text-sm font-medium">{user?.name || user?.email}</div>
              <div className="text-xs text-muted-foreground">{t("dashboard")}</div>
            </div>
            <Button variant="outline" size="sm" className="w-full justify-start" onClick={logout}>
              <LogOut className="h-4 w-4" />
              {t("signOut")}
            </Button>
          </div>
        </div>
      </aside>

      <main className="min-w-0">
        <header className="sticky top-0 z-20 border-b border-[#e2d8cd] bg-white/90 backdrop-blur">
          <div className="flex items-center justify-between gap-4 px-4 py-3 lg:px-8">
            <button onClick={() => navigate("/student")} className="flex items-center gap-3 text-left lg:hidden">
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#d94205] text-sm font-bold text-white">BO</span>
              <span>
                <span className="block font-semibold">Exam Studio</span>
                <span className="block text-xs text-muted-foreground">{t("portal")}</span>
              </span>
            </button>
            <div className="hidden lg:block">
              <div className="text-sm text-muted-foreground">{t("portal")}</div>
              <div className="font-semibold">{t("overviewTitle")}</div>
            </div>
            <div className="flex items-center gap-2">
              <div className="hidden text-right text-sm sm:block">
                <div className="font-medium">{user?.name || user?.email}</div>
                <div className="text-xs text-muted-foreground">{t("dashboard")}</div>
              </div>
              <div className="flex items-center gap-1 rounded-full border bg-white px-2 py-1 shadow-sm">
                <Languages className="h-4 w-4 text-[#d94205]" />
                {(["en", "tr", "de"] as StudentLocale[]).map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setLocale(item)}
                    className={cn(
                      "rounded-full px-2.5 py-1 text-xs font-semibold transition-colors",
                      locale === item ? "bg-[#252525] text-white" : "text-[#555] hover:bg-[#f4eee7]"
                    )}
                  >
                    {item.toUpperCase()}
                  </button>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={logout} className="hidden sm:inline-flex">
                <LogOut className="h-4 w-4" />
                {t("signOut")}
              </Button>
            </div>
          </div>
        </header>
        <div className="mx-auto max-w-7xl px-4 py-6 lg:px-8">{children}</div>
      </main>
    </div>
  );
}

function StudentHome(props: ReturnType<typeof useStudentLocale>) {
  const { t, locale, setLocale } = props;
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState<StudentHomePayload | null>(null);

  useEffect(() => {
    setLoading(true);
    apiFetch<StudentHomePayload>("/api/student/mock-exams")
      .then((data) => setPayload(data))
      .catch((error) => toast.error(error instanceof Error ? error.message : t("loadFailed")))
      .finally(() => setLoading(false));
  }, [locale]);

  const exams = payload?.mockExams || [];
  const stats = payload?.stats || { totalAttempts: 0, completedAttempts: 0, bestPercent: 0, averagePercent: 0, studyMinutes: 0 };
  const hasAccess = Boolean(payload?.hasActiveSubscription);
  const totalQuestions = exams.reduce((sum, exam) => sum + Number(exam.questionCount || 0), 0);
  const totalParts = exams.reduce((sum, exam) => sum + Number(exam.taskCount || 0), 0);
  const skillRows = buildSkillRows(payload?.skillPerformance || [], locale);
  const nextExam = exams[0];

  return (
    <StudentShell locale={locale} setLocale={setLocale} t={t}>
      {loading ? (
        <StudentLoading label={t("loading")} />
      ) : (
        <div className="space-y-6">
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <DashboardStat label={t("myExams")} value={exams.length} icon={ClipboardList} accent="dark" />
            <DashboardStat label={t("completed")} value={stats.completedAttempts} icon={CheckCircle2} />
            <DashboardStat label={t("averageScore")} value={`${stats.averagePercent}%`} icon={Target} />
            <DashboardStat label={t("studyTime")} value={`${stats.studyMinutes} ${t("minutes")}`} icon={Clock3} />
          </section>

          <section className="grid gap-6 xl:grid-cols-[1.4fr_.8fr]">
            <Card className="overflow-hidden border-[#ded6cc] bg-[#252525] text-white shadow-sm">
              <CardHeader className="relative">
                <div className="absolute right-8 top-4 h-32 w-32 rounded-full bg-[#d94205]/25 blur-2xl" />
                <Badge className="mb-4 w-fit bg-[#d94205] text-white hover:bg-[#d94205]">{t("available")}</Badge>
                <CardTitle className="max-w-2xl text-4xl leading-tight lg:text-5xl">{t("overviewTitle")}</CardTitle>
                <CardDescription className="max-w-2xl text-base leading-7 text-white/70">{t("overviewText")}</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3 text-sm sm:grid-cols-3">
                <Metric value={totalQuestions} label={t("questions")} dark />
                <Metric value={totalParts} label={t("parts")} dark />
                <Metric value={`${stats.bestPercent}%`} label={t("best")} dark />
              </CardContent>
            </Card>
            <PackageCard subscription={payload?.subscription || null} hasAccess={hasAccess} t={t} />
          </section>

          <section className="grid gap-6 xl:grid-cols-[1.35fr_.65fr]">
            <Card id="my-exams" className="border-[#ded6cc] bg-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardList className="h-5 w-5 text-[#d94205]" />
                  {t("myExams")}
                </CardTitle>
                <CardDescription>{t("examsDesc")}</CardDescription>
              </CardHeader>
              <CardContent>
                {exams.length ? (
                  <div className="space-y-3">
                    {exams.map((exam) => <ExamRow key={exam.id} exam={exam} t={t} />)}
                  </div>
                ) : (
                  <EmptyStudentState title={t("noExams")} description={t("noExamsDesc")} />
                )}
              </CardContent>
            </Card>

            <div className="space-y-6">
              {nextExam && <ContinueCard exam={nextExam} t={t} />}
              <AccountCard subscription={payload?.subscription || null} hasAccess={hasAccess} t={t} />
            </div>
          </section>

          <section id="performance" className="grid gap-6 xl:grid-cols-[1fr_.8fr]">
            <Card className="border-[#ded6cc] bg-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-[#d94205]" />
                  {t("performanceTitle")}
                </CardTitle>
                <CardDescription>{t("subtitle")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {skillRows.map((skill) => <SkillPerformanceRow key={skill.skillKey} skill={skill} t={t} />)}
              </CardContent>
            </Card>

            <Card className="border-[#ded6cc] bg-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-[#d94205]" />
                  {t("recentActivity")}
                </CardTitle>
                <CardDescription>{t("attempts")}</CardDescription>
              </CardHeader>
              <CardContent>
                {payload?.recentAttempts?.length ? (
                  <div className="space-y-3">
                    {payload.recentAttempts.map((attempt) => <RecentAttemptRow key={attempt.id} attempt={attempt} />)}
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed bg-[#fffaf4] p-5 text-sm text-muted-foreground">{t("noActivity")}</div>
                )}
              </CardContent>
            </Card>
          </section>
        </div>
      )}
    </StudentShell>
  );
}

function StudentExam(props: ReturnType<typeof useStudentLocale>) {
  const { t, locale, setLocale } = props;
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [exam, setExam] = useState<StudentMockDetail | null>(null);
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, AnswerDraft>>({});
  const [result, setResult] = useState<AttemptResult | null>(null);
  const [startedAt] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([
      apiFetch<{ mockExam: StudentMockDetail }>(`/api/student/mock-exams/${id}`),
      postJson<{ attempt: { id: string; startedAt: string } }>("/api/student/attempts", { mockExamId: id }),
    ])
      .then(([detail, attempt]) => {
        setExam(detail.mockExam);
        setAttemptId(attempt.attempt.id);
        toast.success(t("started"));
      })
      .catch((error) => toast.error(error instanceof Error ? error.message : t("loadFailed")))
      .finally(() => setLoading(false));
  }, [id, locale]);

  const flatQuestions = useMemo(() => {
    return (exam?.tasks || []).flatMap((task) =>
      task.questions.map((question) => ({ ...question, task }))
    );
  }, [exam]);

  const current = flatQuestions[currentIndex];
  const answeredCount = flatQuestions.filter((question) => hasAnswer(answers[question.id])).length;
  const durationSec = exam?.template.durationSec || 0;
  const elapsedSec = Math.floor((now - startedAt) / 1000);
  const remainingSec = durationSec ? Math.max(0, durationSec - elapsedSec) : 0;

  const updateAnswer = (questionId: string, answer: AnswerDraft) => {
    setAnswers((currentAnswers) => ({ ...currentAnswers, [questionId]: { ...currentAnswers[questionId], ...answer } }));
  };

  const submit = async () => {
    if (!attemptId || !exam) return;
    if (answeredCount < flatQuestions.length) toast.message(t("unansweredWarning"));
    setSubmitting(true);
    try {
      const payload = flatQuestions.map((question) => ({
        questionId: question.id,
        choiceId: answers[question.id]?.choiceId || null,
        textAnswer: answers[question.id]?.textAnswer || null,
        elapsedMs: elapsedSec * 1000,
      }));
      const data = await postJson<{ result: AttemptResult }>(`/api/student/attempts/${attemptId}`, { answers: payload });
      setResult(data.result);
      toast.success(t("saved"));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("submitFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StudentShell locale={locale} setLocale={setLocale} t={t}>
      {loading || !exam || !current ? (
        <StudentLoading label={t("loading")} />
      ) : (
        <div className="space-y-5">
          <div className="flex flex-col justify-between gap-3 lg:flex-row lg:items-center">
            <div>
              <Button variant="ghost" size="sm" onClick={() => navigate("/student")} className="mb-2 -ml-2">
                <ArrowLeft className="h-4 w-4" />
                {t("back")}
              </Button>
              <h1 className="text-3xl font-bold">{exam.title}</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {exam.template.family.name} / {exam.template.language.nativeName} / {exam.template.levelCode}
              </p>
            </div>
            <div className="grid grid-cols-3 gap-2 sm:min-w-[420px]">
              <Metric value={formatTime(remainingSec)} label={t("timer")} icon={Clock3} />
              <Metric value={`${answeredCount}/${flatQuestions.length}`} label={t("answered")} icon={ListChecks} />
              <Metric value={Math.round(exam.pointTotal)} label={t("points")} icon={Trophy} />
            </div>
          </div>

          {result && <ResultPanel result={result} t={t} onRetake={() => window.location.reload()} />}

          <div className="grid gap-5 lg:grid-cols-[320px_1fr]">
            <Card className="h-fit border-[#ded6cc] bg-white">
              <CardHeader>
                <CardTitle className="text-lg">{t("currentPart")}</CardTitle>
                <CardDescription>{exam.template.name}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {exam.tasks.map((task) => {
                  const firstIndex = flatQuestions.findIndex((question) => question.task.id === task.id);
                  const taskAnswered = task.questions.filter((question) => hasAnswer(answers[question.id])).length;
                  const active = current.task.id === task.id;
                  return (
                    <button
                      key={task.id}
                      type="button"
                      onClick={() => firstIndex >= 0 && setCurrentIndex(firstIndex)}
                      className={cn(
                        "w-full rounded-xl border p-3 text-left transition-colors",
                        active ? "border-[#d94205] bg-[#fff4ec]" : "bg-white hover:bg-[#fffaf4]"
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium">{task.skill.name}</div>
                        <SkillIcon skill={task.skill.key} />
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">{task.sectionName}</div>
                      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#eee4da]">
                        <div
                          className="h-full rounded-full bg-[#d94205]"
                          style={{ width: `${task.questions.length ? (taskAnswered / task.questions.length) * 100 : 0}%` }}
                        />
                      </div>
                    </button>
                  );
                })}
              </CardContent>
            </Card>

            <Card className="border-[#ded6cc] bg-white shadow-sm">
              <CardHeader className="border-b border-[#eee4da]">
                <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
                  <div>
                    <Badge variant="outline" className="mb-3 border-[#d94205] text-[#d94205]">
                      {current.task.skill.name} / {current.task.templateTask.name}
                    </Badge>
                    <CardTitle className="text-2xl">{current.title}</CardTitle>
                    <CardDescription className="mt-2">{current.task.sectionInstructions || current.task.templateTask.prompt}</CardDescription>
                  </div>
                  <Badge className="w-fit bg-[#252525] text-white hover:bg-[#252525]">
                    {currentIndex + 1} / {flatQuestions.length}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-5 pt-5">
                {isListening(current.task.skill.key) && (
                  <div className="rounded-xl border border-[#f0d6c4] bg-[#fff7f0] p-4">
                    <div className="flex items-center gap-2 font-medium">
                      <Headphones className="h-4 w-4 text-[#d94205]" />
                      {t("listeningNote")}
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">{t("listeningHelp")}</p>
                  </div>
                )}

                <div className="rounded-xl bg-[#fffaf4] p-5 text-base leading-8 whitespace-pre-line">
                  {current.stem}
                </div>

                <AnswerInput
                  question={current}
                  answer={answers[current.id] || {}}
                  result={result}
                  t={t}
                  onChange={(answer) => updateAnswer(current.id, answer)}
                />

                <div className="flex flex-col justify-between gap-3 border-t border-[#eee4da] pt-5 sm:flex-row sm:items-center">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentIndex((index) => Math.max(0, index - 1))}
                    disabled={currentIndex === 0}
                  >
                    {t("previous")}
                  </Button>
                  <div className="flex gap-2">
                    {currentIndex < flatQuestions.length - 1 ? (
                      <Button onClick={() => setCurrentIndex((index) => Math.min(flatQuestions.length - 1, index + 1))}>
                        {t("next")}
                      </Button>
                    ) : (
                      <Button onClick={submit} disabled={submitting || Boolean(result)}>
                        {submitting ? t("submitting") : t("submit")}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </StudentShell>
  );
}

function PackageCard({ subscription, hasAccess, t }: { subscription: StudentSubscription; hasAccess: boolean; t: (key: StudentTextKey) => string }) {
  return (
    <Card id="package" className="border-[#ded6cc] bg-white">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {hasAccess ? <ShieldCheck className="h-5 w-5 text-emerald-600" /> : <LockKeyhole className="h-5 w-5 text-[#d94205]" />}
          {t("packageTitle")}
        </CardTitle>
        <CardDescription>{hasAccess ? t("activeLicense") : t("inactiveLicense")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-xl border bg-[#fffaf4] p-4">
          <div className="text-xs text-muted-foreground">{t("plan")}</div>
          <div className="mt-1 text-2xl font-bold">{subscription?.planName || t("subscriptionLocked")}</div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
          <InfoBlock label={t("license")} value={subscription?.licenseKey || "-"} />
          <InfoBlock label={t("validUntil")} value={formatDate(subscription?.currentPeriodEnd)} />
        </div>
        <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4 text-sm text-emerald-900">
          <div className="font-semibold">{t("included")}</div>
          <p className="mt-1 text-emerald-800">{t("includedText")}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function AccountCard({ subscription, hasAccess, t }: { subscription: StudentSubscription; hasAccess: boolean; t: (key: StudentTextKey) => string }) {
  return (
    <Card id="account" className="border-[#ded6cc] bg-white">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserRound className="h-5 w-5 text-[#d94205]" />
          {t("accountTitle")}
        </CardTitle>
        <CardDescription>{t("accountDesc")}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3">
        <InfoBlock label={t("memberType")} value={hasAccess ? t("subscriptionReady") : t("subscriptionLocked")} />
        <InfoBlock label={t("license")} value={subscription?.licenseKey || "-"} />
      </CardContent>
    </Card>
  );
}

function ContinueCard({ exam, t }: { exam: StudentMockExam; t: (key: StudentTextKey) => string }) {
  const action = exam.progress.attemptCount ? t("continue") : t("start");
  return (
    <Card className="border-[#ded6cc] bg-white">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Play className="h-5 w-5 text-[#d94205]" />
          {action}
        </CardTitle>
        <CardDescription>{exam.title}</CardDescription>
      </CardHeader>
      <CardContent>
        <Button asChild className="w-full">
          <Link to={`/student/exam/${exam.id}`}>{t("openExam")}</Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function ExamRow({ exam, t }: { exam: StudentMockExam; t: (key: StudentTextKey) => string }) {
  const progressPercent = Math.round(Number(exam.progress.bestPercent || 0));
  const action = exam.progress.attemptCount ? t("continue") : t("start");

  return (
    <div className="rounded-2xl border bg-white p-4 transition-colors hover:bg-[#fffaf4]">
      <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
        <div className="min-w-0">
          <div className="mb-2 flex flex-wrap gap-2">
            <Badge variant="outline" className="border-[#d94205] text-[#d94205]">
              {exam.template.family.name} / {exam.template.levelCode}
            </Badge>
            <Badge className="bg-[#252525] text-white hover:bg-[#252525]">{exam.template.language.nativeName}</Badge>
          </div>
          <div className="truncate text-lg font-semibold">{exam.title}</div>
          <div className="text-sm text-muted-foreground">{exam.template.name}</div>
        </div>
        <div className="grid gap-2 sm:grid-cols-3 lg:w-[360px]">
          <MiniStat value={exam.questionCount} label={t("questions")} />
          <MiniStat value={exam.taskCount} label={t("parts")} />
          <MiniStat value={Math.round((exam.template.durationSec || 0) / 60)} label={t("minutes")} />
        </div>
        <div className="lg:w-44">
          <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
            <span>{t("best")}</span>
            <span className="font-semibold text-[#171717]">{progressPercent}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-[#eee4da]">
            <div className="h-full rounded-full bg-[#d94205]" style={{ width: `${progressPercent}%` }} />
          </div>
        </div>
        <Button asChild className="lg:w-36">
          <Link to={`/student/exam/${exam.id}`}>{action}</Link>
        </Button>
      </div>
    </div>
  );
}

function SkillPerformanceRow({ skill, t }: { skill: StudentSkillPerformance; t: (key: StudentTextKey) => string }) {
  const performance = skill.performance ?? 0;
  const isManual = skill.performance === null && skill.textAnswerCount > 0;
  return (
    <div className="rounded-xl border bg-[#fffaf4] p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-xl bg-white text-[#d94205] shadow-sm">
            <SkillIcon skill={skill.skillKey} />
          </span>
          <div>
            <div className="font-semibold">{skill.skillName}</div>
            <div className="text-xs text-muted-foreground">
              {isManual ? t("manualReview") : skill.answeredCount ? `${skill.correctCount}/${skill.autoAnsweredCount} ${t("correct")}` : t("notStarted")}
            </div>
          </div>
        </div>
        <div className="text-xl font-bold">{skill.performance === null ? "-" : `${performance}%`}</div>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-[#eadfd3]">
        <div className="h-full rounded-full bg-[#d94205]" style={{ width: `${performance}%` }} />
      </div>
    </div>
  );
}

function RecentAttemptRow({ attempt }: { attempt: StudentRecentAttempt }) {
  return (
    <div className="rounded-xl border bg-[#fffaf4] p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-medium">{attempt.examTitle}</div>
          <div className="mt-1 text-xs text-muted-foreground">{attempt.familyName} / {attempt.levelCode}</div>
        </div>
        <Badge variant="outline">{Math.round(Number(attempt.scoreScaled || 0))}%</Badge>
      </div>
      <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
        <CalendarDays className="h-3.5 w-3.5" />
        {formatDate(attempt.finishedAt || attempt.startedAt)}
      </div>
    </div>
  );
}

function AnswerInput({
  question,
  answer,
  result,
  t,
  onChange,
}: {
  question: StudentQuestion;
  answer: AnswerDraft;
  result: AttemptResult | null;
  t: (key: StudentTextKey) => string;
  onChange: (answer: AnswerDraft) => void;
}) {
  const review = result?.review.find((item) => item.questionId === question.id);
  const isTextTask = question.taskType === "writing" || question.taskType === "speaking" || !question.choices.length;

  if (isTextTask) {
    return (
      <div className="space-y-3">
        {question.taskType === "speaking" && <p className="text-sm text-muted-foreground">{t("speakingNote")}</p>}
        <Textarea
          value={answer.textAnswer || ""}
          onChange={(event) => onChange({ textAnswer: event.target.value })}
          rows={9}
          disabled={Boolean(result)}
          placeholder={t("writeAnswer")}
          className="text-base leading-7"
        />
        {result && <p className="text-sm text-muted-foreground">{t("notAutoScored")}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="text-sm font-medium text-muted-foreground">{t("selectAnswer")}</div>
      {question.choices.map((choice) => {
        const selected = answer.choiceId === choice.id;
        const correct = review?.correctChoiceIds.includes(choice.id);
        const wrongSelection = selected && review?.correct === 0;
        return (
          <button
            key={choice.id}
            type="button"
            disabled={Boolean(result)}
            onClick={() => onChange({ choiceId: choice.id })}
            className={cn(
              "flex w-full items-start gap-3 rounded-xl border p-4 text-left transition-colors",
              selected ? "border-[#d94205] bg-[#fff4ec]" : "bg-white hover:bg-[#fffaf4]",
              result && correct && "border-emerald-300 bg-emerald-50",
              result && wrongSelection && "border-red-300 bg-red-50"
            )}
          >
            <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full border text-xs font-semibold">
              {selected ? <CheckCircle2 className="h-4 w-4" /> : choice.sortOrder}
            </span>
            <span className="leading-7">{choice.text}</span>
            {result && correct && <CheckCircle2 className="ml-auto h-5 w-5 text-emerald-600" />}
            {result && wrongSelection && <XCircle className="ml-auto h-5 w-5 text-red-600" />}
          </button>
        );
      })}
      {result && question.explanation && (
        <div className="rounded-xl border bg-[#fffaf4] p-4 text-sm leading-6 text-muted-foreground">
          {question.explanation}
        </div>
      )}
    </div>
  );
}

function ResultPanel({ result, t, onRetake }: { result: AttemptResult; t: (key: StudentTextKey) => string; onRetake: () => void }) {
  return (
    <Card className="border-emerald-200 bg-emerald-50">
      <CardContent className="flex flex-col justify-between gap-4 p-5 lg:flex-row lg:items-center">
        <div className="flex items-start gap-4">
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-emerald-600 text-white">
            <Trophy className="h-6 w-6" />
          </span>
          <div>
            <div className="text-sm font-semibold uppercase tracking-wide text-emerald-700">{t("resultTitle")}</div>
            <div className="mt-1 text-3xl font-bold text-emerald-950">
              {result.scoreScaled ?? 0}%
            </div>
            <p className="mt-1 text-sm text-emerald-800">
              {result.correctCount}/{result.autoQuestionCount} {t("correct")} / {result.maxAutoScore} {t("points")} {t("autoScored")}
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={onRetake}>{t("retake")}</Button>
      </CardContent>
    </Card>
  );
}

function DashboardStat({ label, value, icon: Icon, accent }: { label: string; value: ReactNode; icon: ComponentType<{ className?: string }>; accent?: "dark" }) {
  return (
    <Card className={cn("border-[#ded6cc]", accent === "dark" ? "bg-[#252525] text-white" : "bg-white") }>
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
        <CardDescription className={accent === "dark" ? "text-white/65" : undefined}>{label}</CardDescription>
        <Icon className={cn("h-4 w-4", accent === "dark" ? "text-[#ffbe2e]" : "text-[#d94205]")} />
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold">{value}</div>
      </CardContent>
    </Card>
  );
}

function SkillIcon({ skill }: { skill: string }) {
  if (isListening(skill)) return <Headphones className="h-4 w-4 text-[#d94205]" />;
  if (["schreiben", "writing"].includes(skill)) return <PenLine className="h-4 w-4 text-[#d94205]" />;
  if (["sprechen", "speaking"].includes(skill)) return <GraduationCap className="h-4 w-4 text-[#d94205]" />;
  return <FileText className="h-4 w-4 text-[#d94205]" />;
}

function Metric({ value, label, icon: Icon, dark }: { value: ReactNode; label: string; icon?: ComponentType<{ className?: string }>; dark?: boolean }) {
  return (
    <div className={cn("rounded-xl border p-3", dark ? "border-white/10 bg-white/10" : "bg-[#fffaf4]") }>
      <div className="flex items-center justify-between gap-2">
        <div className={cn("text-xs", dark ? "text-white/60" : "text-muted-foreground")}>{label}</div>
        {Icon && <Icon className={cn("h-4 w-4", dark ? "text-[#ffbe2e]" : "text-[#d94205]")} />}
      </div>
      <div className="mt-1 truncate text-xl font-bold">{value}</div>
    </div>
  );
}

function MiniStat({ value, label }: { value: ReactNode; label: string }) {
  return (
    <div className="rounded-xl border bg-[#fffaf4] px-3 py-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-bold">{value}</div>
    </div>
  );
}

function InfoBlock({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-xl border bg-[#fffaf4] p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 break-words text-sm font-semibold">{value || "-"}</div>
    </div>
  );
}

function StudentLoading({ label }: { label: string }) {
  return <div className="rounded-xl border bg-white p-5 text-sm text-muted-foreground">{label}</div>;
}

function EmptyStudentState({ title, description }: { title: string; description: string }) {
  return (
    <div className="grid min-h-64 place-items-center rounded-2xl border border-dashed bg-white p-8 text-center">
      <div>
        <BookOpenCheck className="mx-auto mb-3 h-10 w-10 text-[#d94205]" />
        <div className="text-lg font-semibold">{title}</div>
        <p className="mt-1 max-w-md text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

function buildSkillRows(rows: StudentSkillPerformance[], locale: StudentLocale) {
  const labels = [
    { keys: ["lesen", "reading"], label: locale === "tr" ? "Okuma" : locale === "de" ? "Lesen" : "Reading" },
    { keys: ["hoeren", "listening"], label: locale === "tr" ? "Dinleme" : locale === "de" ? "Hoeren" : "Listening" },
    { keys: ["sprachbausteine", "language_use"], label: locale === "tr" ? "Dil bilgisi" : locale === "de" ? "Sprachbausteine" : "Language use" },
    { keys: ["schreiben", "writing"], label: locale === "tr" ? "Yazma" : locale === "de" ? "Schreiben" : "Writing" },
    { keys: ["sprechen", "speaking"], label: locale === "tr" ? "Konusma" : locale === "de" ? "Sprechen" : "Speaking" },
  ];
  const byKey = new Map(rows.map((row) => [row.skillKey, row]));
  return labels.map((item) => ({
    skillKey: item.keys[0],
    skillName: item.label,
    answeredCount: item.keys.reduce((sum, key) => sum + Number(byKey.get(key)?.answeredCount || 0), 0),
    autoAnsweredCount: item.keys.reduce((sum, key) => sum + Number(byKey.get(key)?.autoAnsweredCount || 0), 0),
    correctCount: item.keys.reduce((sum, key) => sum + Number(byKey.get(key)?.correctCount || 0), 0),
    textAnswerCount: item.keys.reduce((sum, key) => sum + Number(byKey.get(key)?.textAnswerCount || 0), 0),
    performance: calculateCombinedPerformance(item.keys.map((key) => byKey.get(key)).filter(Boolean) as StudentSkillPerformance[]),
  }));
}

function calculateCombinedPerformance(rows: StudentSkillPerformance[]) {
  const autoAnsweredCount = rows.reduce((sum, row) => sum + Number(row.autoAnsweredCount || 0), 0);
  if (!autoAnsweredCount) return null;
  const correctCount = rows.reduce((sum, row) => sum + Number(row.correctCount || 0), 0);
  return Math.round((correctCount / autoAnsweredCount) * 100);
}

function hasAnswer(answer?: AnswerDraft) {
  return Boolean(answer?.choiceId || answer?.textAnswer?.trim());
}

function isListening(skill: string) {
  return ["hoeren", "listening"].includes(skill);
}

function formatTime(seconds: number) {
  const safeSeconds = Math.max(0, seconds);
  const mins = Math.floor(safeSeconds / 60);
  const secs = safeSeconds % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

function formatDate(value?: string | null) {
  if (!value) return "-";
  return new Intl.DateTimeFormat("en", { year: "numeric", month: "short", day: "2-digit" }).format(new Date(value));
}
